// Database Gateways (/api/gateways) — on-prem agents a tenant pairs to
// twasta so analytics can query databases inside customer firewalls. A
// gateway connects OUTBOUND (wss) and announces logical database aliases;
// BI connections of type 'gateway' bind {GATEWAY_ID, DATABASE_ALIAS,
// REMOTE_DIALECT}. Credentials never reach twasta.
import api from './client'

export interface GatewayDatabase {
  alias: string
  dbtype?: string
  label?: string
  last_announced_at?: string | null
}

export interface Gateway {
  id: string
  name: string
  status: 'pending' | 'active' | 'revoked'
  online: boolean
  in_flight?: number
  last_seen_at?: string | null
  gateway_version?: string | null
  pairing_pending?: boolean
  created_at?: string | null
  created_by?: string | null
  databases: GatewayDatabase[]
}

export interface GatewayAccessSummary {
  since: string
  by_user: { user_email: string; queries: number; rows: number }[]
  by_alias: { alias: string; queries: number; rows: number }[]
  by_day: { day: string; requests: number }[]
  by_status: { status: string; requests: number }[]
}

export async function listGateways(): Promise<Gateway[]> {
  const res = await api.get('/gateways')
  return (res.data?.gateways ?? []) as Gateway[]
}

/** Creates the gateway and returns the ONE-TIME pairing token — it is never
 * retrievable again (only its hash is stored server-side). */
export async function createGateway(name: string): Promise<{ gateway: Gateway; pairing_token: string }> {
  const res = await api.post('/gateways', { name })
  return { gateway: res.data?.gateway as Gateway, pairing_token: res.data?.pairing_token as string }
}

export async function rotateGateway(gatewayId: string): Promise<string> {
  const res = await api.post(`/gateways/${gatewayId}/rotate`)
  return res.data?.pairing_token as string
}

export async function revokeGateway(gatewayId: string): Promise<void> {
  await api.post(`/gateways/${gatewayId}/revoke`)
}

export async function listGatewayDatabases(
  gatewayId: string,
): Promise<{ databases: GatewayDatabase[]; online: boolean; status: string }> {
  const res = await api.get(`/gateways/${gatewayId}/databases`)
  return {
    databases: (res.data?.databases ?? []) as GatewayDatabase[],
    online: !!res.data?.online,
    status: (res.data?.status ?? '') as string,
  }
}

/** Aggregate-only access stats — available only when the gateway operator
 * enabled remote_audit_view on at least one database alias. */
export async function gatewayAccessSummary(
  gatewayId: string, sinceDays = 30,
): Promise<GatewayAccessSummary> {
  const res = await api.get(`/gateways/${gatewayId}/access-summary`, {
    params: { since_days: sinceDays },
  })
  return res.data?.summary as GatewayAccessSummary
}

/** Download the self-installing gateway package zip (blob → anchor click,
 * same pattern as downloadRuntimePackage). */
export async function downloadGatewayPackage(): Promise<string> {
  const res = await api.post('/gateways/package', {}, { responseType: 'blob' })
  const dispo = (res.headers?.['content-disposition'] as string) || ''
  const m = /filename="?([^";]+)"?/.exec(dispo)
  const filename = m?.[1] || 'twasta-gateway.zip'
  const blob = new Blob([res.data], { type: 'application/zip' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
  return filename
}
